/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * W116 — THE SET HAS ITS VIDEO (his word, 2026-09-30 ~22:10): "anywhere that is wether in coach, client, client progress
 * page or when he works out, anywhere, where there is points (logged workouts) with numbers, there needs to be the videos
 * if any were uploaded. so just a play button like the coach has when he reviews a workout."
 *
 * THE RULE, ONE PLACE (the phone's twin is `SetClips.swift`): wherever a LOGGED set's numbers are printed, the clips filed
 * against that set (`form_clips`, keyed on `(workout_session_exercise_id, set_number)`) sit behind ONE round play button
 * (`ClipPlayButton`), which opens the ONE player (its stage). No clip → nothing drawn. PURE; `setClips.test.ts` runs it.
 *
 * ⚠️ A SET MAY CARRY SEVERAL CLIPS ON PURPOSE (no unique constraint on the pair; MEASURED live 2026-10-01: 14 rows over 12
 * pairs). A line plays ALL of its clips, in set order, then oldest first, ids breaking ties — the review's own walk
 * (`reviewSheet.clipsOf`), so every surface steps a set's takes the same way.
 * ══════════════════════════════════════════════════════════════════════════════════════════════ */

export type SetClip = {
  readonly id: string;
  readonly storagePath: string;
  readonly mediaKind: string;
  readonly setNumber: number | null;
  readonly recordedAtISO: string | null;
  /** Place in a PICK (`form_clips.position`, 0-based); null/absent for a clip the camera filmed. Orders items one pick
   *  shares an instant with — `media.orderMedia`'s rule, the twin of the phone's `MediaAttachment.inPickOrder`. */
  readonly position?: number | null;
};

/** Set order (a clip naming no set last), then oldest first, then the pick's own order (a filmed clip after the picks it
 *  shares an instant with), then id. Returns a new array. */
export function orderClips<C extends SetClip>(clips: readonly C[]): C[] {
  return [...clips].sort((a, b) => {
    const sa = a.setNumber ?? Number.POSITIVE_INFINITY, sb = b.setNumber ?? Number.POSITIVE_INFINITY;
    if (sa !== sb) return sa - sb;
    const ra = a.recordedAtISO ?? "", rb = b.recordedAtISO ?? "";
    if (ra !== rb) return ra < rb ? -1 : 1;
    const pa = a.position ?? null, pb = b.position ?? null;
    if (pa !== null && pb !== null && pa !== pb) return pa - pb;
    if (pa !== null && pb === null) return -1;
    if (pa === null && pb !== null) return 1;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });
}

/** The clips of ONE logged set — the button on that set's own line. */
export function clipsOfSet<C extends SetClip>(clips: readonly C[], setNumber: number): C[] {
  return orderClips(clips.filter((c) => c.setNumber === setNumber));
}

/** The clips a SUMMARY line plays (`3 × 8 @ 60 kg` for a whole exercise instance): every clip of the instances it
 *  describes, in set order. The player opens on the first and steps through the rest. */
export function clipsOfLine<C extends SetClip>(byInstance: ReadonlyMap<string, readonly C[]> | undefined, instanceIds: readonly string[]): C[] {
  return orderClips(instanceIds.flatMap((id) => [...(byInstance?.get(id) ?? [])]));
}

/** THE BATCH: one `form_clips` read for a screen's exercise instances comes back as rows; this files them under their
 *  instance (`workout_session_exercise_id` — the key every line looks them up by). */
export function groupByInstance<C>(rows: readonly C[], instanceOf: (row: C) => string): Map<string, C[]> {
  const out = new Map<string, C[]>();
  for (const r of rows) {
    const k = instanceOf(r);
    out.set(k, [...(out.get(k) ?? []), r]);
  }
  return out;
}

/** The ONE accessible name (no words are DRAWN beside the glyph — his text-as-short-as-possible rule). `setLabel` is the
 *  line's own name for its set (`2`, `W1`); null on a summary line, which names its sets from the clips. */
export function playLabel(setLabel: string | null, clips: readonly { readonly setNumber: number | null }[]): string {
  const noun = clips.length === 1 ? "clip" : "clips";
  if (setLabel !== null) return `Play set ${setLabel} ${noun}`;
  const sets = [...new Set(clips.map((c) => c.setNumber).filter((n): n is number => n !== null))];
  if (sets.length === 1) return `Play set ${String(sets[0])} ${noun}`;
  return clips.length === 1 ? "Play clip" : `Play ${String(clips.length)} clips`;
}
